import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LucideAngularModule } from 'lucide-angular';
import { AuthService } from '../../core/services/auth.service';

/** サイドメニューの 1 項目 */
interface MenuItem {
  label: string;
  icon: string;
  link: string;
  /** この項目がアクティブとみなす URL 判定 */
  match: (url: string) => boolean;
}

/** サイドメニューのグループ */
interface MenuGroup {
  group: string;
  items: MenuItem[];
}

/**
 * 全画面共通のベースレイアウト。
 * ヘッダー + せり出し式サイドメニュー + メインコンテンツ（router-outlet）で構成する。
 */
@Component({
  selector: 'app-main-layout',
  imports: [RouterOutlet, RouterLink, LucideAngularModule],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.scss',
})
export class MainLayout {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  /** サイドメニューの開閉状態（初期は開いた状態） */
  readonly isSidebarOpen = signal(true);

  /** 現在の URL（ナビゲーション完了ごとに更新） */
  private readonly currentUrl = signal(this.router.url);

  /** メニュー定義 */
  private readonly menuConfig: MenuGroup[] = [
    {
      group: 'メイン',
      items: [
        {
          label: 'ダッシュボード',
          icon: 'layout-dashboard',
          link: '/',
          match: (url) => url === '/' || url === '',
        },
      ],
    },
    {
      group: '検査マスタ',
      items: [
        {
          label: '検査マスタ登録',
          icon: 'file-plus',
          link: '/master/register',
          match: (url) => url.startsWith('/master/register'),
        },
        {
          label: 'マスタ確認',
          icon: 'list-checks',
          link: '/master/check',
          match: (url) => url.startsWith('/master/check') || url.startsWith('/master/edit'),
        },
      ],
    },
    {
      group: '分類作成',
      items: [
        {
          label: '分類登録',
          icon: 'folder-plus',
          link: '/category/register',
          match: (url) => url.startsWith('/category/register'),
        },
        {
          label: '分類確認',
          icon: 'folder-search',
          link: '/category/check',
          match: (url) => url.startsWith('/category/check') || url.startsWith('/category/edit'),
        },
      ],
    },
    {
      group: '分類活用',
      items: [
        {
          label: '複数分類でAND絞り込み',
          icon: 'list-filter',
          link: '/category/filter',
          match: (url) => url.startsWith('/category/filter'),
        },
      ],
    },
  ];

  /** URL に応じてアクティブ状態・シェブロン表示を付与したメニュー（テンプレート用） */
  readonly menuGroups = computed(() => {
    const url = this.currentUrl();
    const isEditUrl = url.includes('/edit/');
    return this.menuConfig.map((g) => ({
      group: g.group,
      items: g.items.map((item) => {
        const isActive = item.match(url);
        return {
          ...item,
          isActive,
          // 編集画面では右シェブロンを出さない（プロトタイプ挙動）
          showChevron: isActive && !isEditUrl,
        };
      }),
    }));
  });

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
  }

  /** サイドメニューの開閉トグル */
  toggleSidebar(): void {
    this.isSidebarOpen.update((v) => !v);
  }

  /** ログアウト。認証状態を破棄してログイン画面へ遷移する。 */
  logout(): void {
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }
}
